import { React, memo } from 'react'
import { SimpleTable, Box, Text, Badge, Stack, FileViewer, ImageViewer, VideoViewer } from '@/components/lib'
import utils, { likertAnswers, likertTotal, tableColumns, ratingPoints, ratingLabelFor } from '@/utils/utils'

import BMITag from './BMITag'

// How one answer is shown, given its data type. Memoised so value rendering is
// isolated and only re-runs when its own props change; shared by the response
// viewer and the response history.
const ResponseValueCell = memo(function ResponseValueCell ({ dataType, value }) {
  if (value === null || value === undefined) return null

  if (['text', 'paragraph', 'integer', 'float', 'slider', 'calculation'].indexOf(dataType.type) > -1) {
    return <Text>{value.toString()}</Text>
  }

  if (dataType.type === 'rating') {
    // The number alone says very little — "4" means nothing without knowing
    // whether the scale ran to five or to six, or what the fourth point was
    // called. The label is not stored with the response, so it is read from the
    // field as it stands now, which is also what makes re-wording one apply
    // retroactively.
    const label = ratingLabelFor(dataType, value)
    return (
      <Stack direction='row' gap={2} alignItems='baseline'>
        <Text>{value} of {ratingPoints(dataType)}</Text>
        {label && <Badge colorPalette='blue' variant='outline'>{label}</Badge>}
      </Stack>
    )
  }

  if (dataType.type === 'choice') {
    return value.map(chosen => <Badge key={chosen} colorPalette='blue' variant='outline'>{chosen}</Badge>)
  }

  if (dataType.type === 'likert') {
    const answers = likertAnswers(dataType, value)
    if (!answers.length) return null
    const total = likertTotal(value, dataType)
    return (
      <Stack gap={1}>
        {answers.map(answer => (
          <Stack key={answer.key} direction='row' gap={2} alignItems='baseline'>
            <Text fontSize='sm' color='fg.muted'>{answer.label}</Text>
            <Badge colorPalette='blue' variant='outline'>{answer.answerLabel}</Badge>
          </Stack>
        ))}
        {total !== null && <Text fontSize='xs' color='fg.muted'>Total: {total}</Text>}
      </Stack>
    )
  }

  if (dataType.type === 'table') {
    const columns = tableColumns(dataType)
    const rows = Array.isArray(value) ? value : []
    if (!columns.length || !rows.length) return null
    return (
      <Box overflowX='auto'>
        <SimpleTable
          size='sm'
          headers={columns.map(column => column.name)}
          rows={rows.map(row => columns.map(column => {
            const cell = (row ?? {})[column.id]
            if (cell === null || cell === undefined || cell === '') return ''
            return column.type === 'date' ? utils.formatDate(cell) : String(cell)
          }))}
        />
      </Box>
    )
  }

  if (dataType.type === 'date') return <Text>{utils.formatDate(value)}</Text>
  if (dataType.type === 'datetime') return <Text>{utils.formatDateTime(value)}</Text>
  if (dataType.type === 'time') return <Text>{utils.formatTime(value)}</Text>
  if (dataType.type === 'temperature') return <Text>{parseFloat(value)?.toFixed(2)} °C</Text>
  if (dataType.type === 'weight') return <Text>{parseFloat(value)?.toFixed(2)} kg</Text>
  if (dataType.type === 'height') return <Text>{parseFloat(value)?.toFixed(2)} cm</Text>
  if (dataType.type === 'file') return <FileViewer url={value.url} name={value.fileName} displayName={value.name} preview />
  if (dataType.type === 'image') return <ImageViewer url={value.url} withPreview />
  if (dataType.type === 'video') return <VideoViewer url={value.url} thumbUrl={value.thumbUrl} withPreview isProcessing={value.needsVideoProcess} />
  if (dataType.type === 'food') return <Text>{value.amountConsumed}g {value.name}</Text>
  if (dataType.type === 'bmi') return <BMITag bmi={value?.bmi} />

  return null
})

export default ResponseValueCell
